import type { BulletDef, PrimitiveGeo, StatMod } from './types';

export interface PetDef {
  id: string;
  name: string;
  description: string;
  cost: number;
  geo: PrimitiveGeo;
  color: number;
  orbitRadius: number;
  fireInterval: number;
  range: number;
  damage: number;
  bullet: BulletDef;
  role: 'support' | 'assault' | 'control';
  ability: { name: string; description: string; shots: number; spread: number };
  passive: { name: string; description: string; mods: StatMod[] };
  unlock?: { type: 'anchor'; era: number } | { type: 'paradox'; level: number };
  houseBonus: { name: string; description: string; mods?: StatMod[]; rerolls?: number; startingHeals?: number; banishes?: number };
}

export type PetAssignmentStation = 'chronolab' | 'garden' | 'wardrobe';
export const PET_ASSIGNMENT_STATIONS: ReadonlyArray<{ id: PetAssignmentStation; name: string }> = [
  { id: 'chronolab', name: 'Chronolab Desk' },
  { id: 'garden', name: 'Temporal Garden' },
  { id: 'wardrobe', name: 'Armory Watch' },
];

export const PETS: PetDef[] = [
  {
    id: 'trilobite', name: 'Chrono Trilobite', description: 'A steady companion that fires compact temporal shards.', cost: 140,
    geo: 'blob', color: 0x58d7ff, orbitRadius: 1.8, fireInterval: 1.15, range: 12, damage: 5,
    bullet: { speed: 13, lifetime: 1.1, radius: 0.13, damage: 1, visual: 'shard', color: 0x65e8ff },
    role: 'support',
    ability: { name: 'Time Shard', description: 'Fires a reliable long-lived temporal shard.', shots: 1, spread: 0 },
    passive: { name: 'Ancient Instinct', description: '+12% pickup radius.', mods: [{ stat: 'pickupRadius', mul: 1.12 }] },
    houseBonus: { name: 'Archive Curator', description: '+5% XP while assigned at home.', mods: [{ stat: 'xpMult', mul: 1.05 }] },
  },
  {
    id: 'raptor', name: 'Pocket Raptor', description: 'An eager hunter with quick, short-ranged volleys.', cost: 260,
    geo: 'spike', color: 0xffb34f, orbitRadius: 2.2, fireInterval: 0.72, range: 10, damage: 3.6,
    bullet: { speed: 16, lifetime: 0.8, radius: 0.11, damage: 1, visual: 'needle', color: 0xffc166 },
    role: 'assault',
    ability: { name: 'Pack Volley', description: 'Fires a close-range three-shot fan.', shots: 3, spread: 0.16 },
    passive: { name: 'Predator Rhythm', description: '+4% player fire rate.', mods: [{ stat: 'fireRate', mul: 1.04 }] },
    houseBonus: { name: 'Yard Scout', description: '+3% move speed while assigned at home.', mods: [{ stat: 'moveSpeed', mul: 1.03 }] },
  },
  {
    id: 'clockwork_owl', name: 'Clockwork Owl', description: 'A late-era familiar whose slow homing bolts patrol a wide area.', cost: 480,
    geo: 'octa', color: 0xd3a6ff, orbitRadius: 2.6, fireInterval: 1.5, range: 16, damage: 8,
    bullet: { speed: 10, lifetime: 1.8, radius: 0.16, damage: 1, homing: 2.4, visual: 'orb', color: 0xd6afff },
    role: 'control',
    ability: { name: 'Survey Bolt', description: 'Launches paired homing bolts across a wide patrol radius.', shots: 2, spread: 0.09 },
    passive: { name: 'Clockwork Survey', description: '+6% critical chance.', mods: [{ stat: 'critChance', add: 0.06 }] },
    houseBonus: { name: 'Probability Scribe', description: '+1 starting reroll while assigned at home.', rerolls: 1 },
  },
  {
    id: 'mammoth_calf', name: 'Frost Mammoth Calf', description: 'A sturdy Ice Age companion that launches chilling snowbursts.', cost: 0,
    geo: 'capsule', color: 0xb9e8f4, orbitRadius: 2.25, fireInterval: 1.3, range: 12, damage: 6.5,
    bullet: { speed: 11, lifetime: 1.25, radius: 0.18, damage: 1, homing: 1.2, visual: 'orb', color: 0xbcefff },
    role: 'support',
    ability: { name: 'Snowburst', description: 'Launches paired slow homing snowbursts.', shots: 2, spread: 0.12 },
    passive: { name: 'Warm Shelter', description: '+8% max HP.', mods: [{ stat: 'maxHp', mul: 1.08 }] },
    unlock: { type: 'anchor', era: 3 },
    houseBonus: { name: 'Garden Tender', description: '+1 run-start healing pickup while assigned at home.', startingHeals: 1 },
  },
  {
    id: 'sun_scarab', name: 'Sun Scarab', description: 'An Ancient Empires guardian that fires a disciplined solar fan.', cost: 0,
    geo: 'disc', color: 0xf1c85c, orbitRadius: 2, fireInterval: 0.9, range: 13, damage: 4.8,
    bullet: { speed: 16, lifetime: 1, radius: 0.12, damage: 1, visual: 'shard', color: 0xffdf70 },
    role: 'assault',
    ability: { name: 'Solar Fan', description: 'Fires four tightly spaced solar shards.', shots: 4, spread: 0.1 },
    passive: { name: 'Gilded Guard', description: '+1 armor.', mods: [{ stat: 'armor', add: 1 }] },
    unlock: { type: 'anchor', era: 5 },
    houseBonus: { name: 'Armory Keeper', description: '+1 armor while assigned at home.', mods: [{ stat: 'armor', add: 1 }] },
  },
  {
    id: 'echo_wisp', name: 'Paradox Wisp', description: 'A living fracture that bends shots toward distant targets.', cost: 0,
    geo: 'torus', color: 0xbe8cff, orbitRadius: 2.8, fireInterval: 1.45, range: 17, damage: 7.2,
    bullet: { speed: 9, lifetime: 2, radius: 0.15, damage: 1, homing: 3.2, homingDelay: 0.15, visual: 'ring', color: 0xc9a7ff },
    role: 'control',
    ability: { name: 'Fracture Echo', description: 'Launches three delayed homing rings.', shots: 3, spread: 0.18 },
    passive: { name: 'Temporal Wake', description: '+12% temporal chance.', mods: [{ stat: 'temporalChance', add: 0.12 }, { stat: 'temporalSlow', add: 0.15 }] },
    unlock: { type: 'paradox', level: 1 },
    houseBonus: { name: 'Shard Diviner', description: '+5% Chronoshards while assigned at home.', mods: [{ stat: 'shardMult', mul: 1.05 }] },
  },
];

export const PET_BY_ID = new Map(PETS.map((pet) => [pet.id, pet]));

export function assignedPetBonuses(ids: readonly string[]): { mods: StatMod[]; rerolls: number; startingHeals: number; banishes: number } {
  const out = { mods: [] as StatMod[], rerolls: 0, startingHeals: 0, banishes: 0 };
  for (const id of new Set(ids)) {
    const bonus = PET_BY_ID.get(id)?.houseBonus;
    if (!bonus) continue;
    out.mods.push(...(bonus.mods ?? []));
    out.rerolls += bonus.rerolls ?? 0;
    out.startingHeals += bonus.startingHeals ?? 0;
    out.banishes += bonus.banishes ?? 0;
  }
  return out;
}
